import { useQuery } from '@tanstack/react-query'
import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import SingleDropDown, {
  DropdownValue,
} from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import Processing from 'ext-ui/components/Processing/Processing'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import { CountryCode, isSupportedCountry } from 'libphonenumber-js'
import React, { ChangeEvent, FormEvent, ReactNode, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectIssuerIdFromState } from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import {
  amexCardTypes,
  amexIssuerCodes,
} from 'src/app/RegisterAccount/RegisterAccount'
import RadioItems from 'src/components/RadioItems/RadioItems'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { Issuers, SupportedBrands } from 'src/enums'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { IssuerIntegratorListModel } from 'src/models/issuerIntegrator.models'
import {
  countriesQueries,
  toCountryListModelMap,
  toCountryOptions,
} from 'src/queries/countries/countries.queries'
import { JPM_ISSUER_ID } from 'src/queries/issuers/issuerIntegrators/issuerIntegrators.models'
import {
  issuerIntegratorQueries,
  toIssuerIntegratorListModel,
} from 'src/queries/issuers/issuerIntegrators/issuerIntegrators.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { useWhitelabel } from 'src/utils/whitelabel.utils'

import { getAddressFieldPlaceholder } from './addressFieldLocalization'
import style from './AddressForm.style'

export type AddressFormFields = Record<string, string> & {
  cardName: string
  /** For Amex only */
  cardType?: string
}

export type AddressFormProps = {
  className?: string
  onSubmit?: (
    values: AddressFormFields,
    additionalIssuerFields?: { [key: string]: string },
  ) => void
  previousElement?: ReactNode
  initialValues?: Partial<AddressFormFields>
  /**
   * Pass in any additional fields for this specific issuer here.
   * These fields will be rendered at the bottom of the form.
   * "cardNumber", "cvc", and "expires" will not be rendered, as
   * the previous form already has those fields
   */
  issuerFields?: IssuerIntegratorListModel['schema']
}

export default function AddressForm(props: AddressFormProps) {
  const {
    className = '',
    onSubmit,
    previousElement,
    initialValues = {},
    issuerFields,
  } = props
  const { whiteLabelIncludes } = useWhitelabel()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { data: allCountryOptions = [], isSuccess: countriesLoaded } = useQuery(
    {
      ...countriesQueries.list(locale),
      select: toCountryOptions,
    },
  )
  const { data: countrysMap = {} } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryListModelMap,
  })
  const user = useCurrentUser()
  // JPM has no BIN ranges, so the issuer id is fixed; otherwise it comes
  // from the BIN-range lookup. The detail query dedupes with parent pages
  // via the shared ['issuers', id] cache key (and also removes the legacy
  // conditional-hook pattern this branch used to have).
  const binRangeIssuerId = useSelector(selectIssuerIdFromState)
  const issuerId = whiteLabelIncludes(SupportedBrands.Jpm)
    ? JPM_ISSUER_ID
    : binRangeIssuerId
  const { data: selectedIssuer } = useQuery({
    ...issuerIntegratorQueries.detail(issuerId),
    select: toIssuerIntegratorListModel,
  })
  const translate = useTranslate()

  // The query above self-fetches (the legacy version waited on a parent page
  // to dispatch the fetch); keep showing the loading state until the country
  // list has loaded, exactly as the legacy state machine did.
  const loadingCountries = !countriesLoaded

  // Filter out fields we have already gathered information on
  const additionalFields = (issuerFields || []).filter((extraField) => {
    return !['cardNumber', 'expires', 'cvc'].includes(extraField.name)
  })

  const additionalFieldKeys = additionalFields.reduce(
    (acc, current) => {
      acc[current.name] = ''
      return acc
    },
    {} as { [key: string]: string },
  )

  const { fields, setFields, fieldsErrors, setFieldsErrors } =
    useFormFields<AddressFormFields>(
      {
        cardName: '',
        city: '',
        address1: '',
        address2: '',
        province: '',
        postal: '',
        country: isSupportedCountry(locale.slice(-2).toUpperCase())
          ? (locale.slice(-2).toUpperCase() as CountryCode)
          : 'US',
        cardholderName: user?.fullName || '',
        ...additionalFieldKeys,
        ...initialValues,
      },
      (): FormFieldsValidation<AddressFormFields> => ({
        // We need to do our own address validation instead of
        // using what comes with the hook since each field is dynamically
        // generated from country information along with how those values
        // are validated.
        cardName: [],
        cardholderName: [],
        city: [],
        country: [],
        address1: [],
        address2: [],
        province: [],
        postal: [],
      }),
    )

  const isAmexIssuer = amexIssuerCodes.includes(selectedIssuer?.code as Issuers)

  const selectedCardType = amexCardTypes.filter(
    (cardType) => cardType.value === fields.cardType,
  )[0]
  const selectedCardTypeIndex = amexCardTypes.indexOf(selectedCardType)

  const [selectedIndex, setSelectedIndex] = useState(selectedCardTypeIndex)

  const handleRadioChange = (index: number) => {
    const value = amexCardTypes[index].value

    setFields({
      ...fields,
      cardType: value,
    })
    setSelectedIndex(index)
  }

  const handleSubmit = () => {
    const additionalErrors: Partial<Record<keyof AddressFormFields, string[]>> =
      {}
    // Run validation against each dynamic field for the selected country
    countrysMap[fields.country].fields.forEach((countryField) => {
      const fieldName = countryField.name as keyof AddressFormFields

      const valueToValidate = fields[fieldName]
      if (!countryField.optional && valueToValidate?.length === 0) {
        additionalErrors[fieldName] = [translate('Must not be blank')]
      }
      if (!countryField.optional && countryField.format) {
        const regex = new RegExp(countryField.format)

        if (!regex.test(fields[fieldName] || '')) {
          additionalErrors[fieldName] = [translate('Invalid value')]
        }
      }
    })

    // For some reason, city is not required according to the server field values.
    // Going to put that validation in here manually for now.
    if (fields['city']?.length === 0) {
      additionalErrors['city'] = [translate('Must not be blank')]
    }

    // Card Display Name must be evaluated separately
    if (fields.cardName.length === 0) {
      additionalErrors.cardName = [translate('Must not be blank')]
    }

    if (isAmexIssuer && !fields.cardType) {
      additionalErrors.cardType = [translate('Must not be blank')]
    }
    const extractedAdditionalFields: { [key: string]: string } = {}
    if (additionalFields.length > 0) {
      for (const extraField of additionalFields) {
        if (!fields[extraField.name] && extraField.required) {
          additionalErrors[extraField.name] = [translate('Must not be blank')]
        }
        extractedAdditionalFields[extraField.name] = fields[extraField.name]
      }
    }

    if (Object.keys(additionalErrors).length !== 0) {
      // VALIDATION FAILED
      setFieldsErrors(additionalErrors)
      return
    }

    onSubmit?.(fields, extractedAdditionalFields)
  }

  // TODO: Refactor this to its own hook to get the list of countries
  const countryOptions = (
    selectedIssuer?.restrictions?.country !== undefined
      ? allCountryOptions.filter((opt) =>
          selectedIssuer.restrictions?.country.includes(opt.value),
        )
      : allCountryOptions
  ).map((singleCountry) => {
    return {
      value: singleCountry.value,
      label: singleCountry.details.text,
    }
  })

  const handleFormChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.currentTarget
    setFields({
      ...fields,
      [name]: value,
    })
  }

  const handleSelectChange = ({ value }: DropdownValue) => {
    setFields({
      ...fields,
      province: value,
    })
  }

  return (
    <form
      className={cn('BillingAddressForm', className, 'flex-column', 'gap-10')}
      css={style}
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        handleSubmit()
      }}
    >
      {loadingCountries ? (
        <Processing />
      ) : (
        <div className="flex-column gap-10">
          <div className="flex-column gap-5">
            <p className="font-h3">{translate('Card Display Name')}</p>
            <ExtUITextInput
              name="cardName"
              testId="cardName"
              maxLength={255}
              value={fields.cardName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const { name, value } = e.currentTarget
                setFields({
                  ...fields,
                  [name]: value,
                })
              }}
              placeholder={translate('Company Card')}
              errorText={fieldsErrors.cardName}
              microcopy={translate(
                "Your card's display name appears at the top of the card detail screen, as well as anywhere that refers to the card. You can update it at any time.",
              )}
            />
          </div>
          <div className="flex-column gap-10">
            <div className="flex-column gap-5">
              <p className="font-h3">{translate('Credit Card Details')}</p>
              {isAmexIssuer && (
                <div>
                  <p className="font-support-medium amex-text">
                    {translate(
                      'Telling us your card type will help us speed up this registration',
                    )}
                  </p>
                  <RadioItems
                    items={amexCardTypes.map((cardType) => (
                      <span
                        key={cardType.value}
                        data-testid={`radio-button-${cardType.value.replace(/_/g, '-')}`}
                      >
                        <p className="font-regular">{cardType.fieldText}</p>
                      </span>
                    ))}
                    currentIndex={selectedIndex}
                    setIndex={handleRadioChange}
                    horizontalAlign
                    staticOnHover
                    noBorder
                    alignStart
                    gap={'40px'}
                    errors={fieldsErrors['cardType' as keyof AddressFormFields]}
                    ariaLabels={amexCardTypes.map((ct) =>
                      typeof ct.fieldText === 'string'
                        ? translate(ct.fieldText)
                        : translate(ct.fieldText.props.source),
                    )}
                  />
                </div>
              )}
              <ExtUITextInput
                name="cardholderName"
                testId="cardholderName"
                value={fields.cardholderName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const { name, value } = e.currentTarget
                  setFields({
                    ...fields,
                    [name]: value,
                  })
                }}
                placeholder={
                  whiteLabelIncludes(SupportedBrands.Jpm)
                    ? translate('Name of the person on the account')
                    : translate('Name of person on the card')
                }
                label={translate('Cardholder Name')}
                errorText={fieldsErrors.cardholderName}
              />
              <SingleDropDown
                selectedValue={countryOptions.find((opt) => {
                  return opt.value === fields.country
                })}
                label={translate('Country')}
                options={countryOptions}
                placeholder={translate('Enter your country')}
                searchable
                onSelect={({ value }) => {
                  setFields({
                    ...fields,
                    country: value,
                    // On country select, clear out province/state
                    province: '',
                  })
                }}
                errorText={fieldsErrors.country}
                autoComplete={'country'}
                data-testid="country"
              />
              {fields.country &&
                countrysMap[fields.country].fields.map((countryField) => {
                  const { options, label, name } = countryField
                  // Addressulator already localized `label` and option labels
                  // for the requested locale. Render them directly; the
                  // shared helper only translates app-owned placeholder copy.
                  const placeholder = getAddressFieldPlaceholder(
                    fields.country,
                    countryField,
                    translate,
                  )
                  // Return a dropdown element if 'options' is available
                  if (options) {
                    const selectedOption = options.find((opt) => {
                      return opt.code === fields.province
                    })
                    return (
                      <SingleDropDown
                        key={name}
                        data-testid="state"
                        selectedValue={
                          selectedOption && {
                            label: selectedOption.label,
                            value: selectedOption.code,
                          }
                        }
                        label={label}
                        options={options.map((opt) => {
                          return {
                            value: opt.code,
                            label: opt.label,
                          }
                        })}
                        searchable
                        onSelect={handleSelectChange}
                        // We know this is the only other dropdown in the country list
                        // so it's safe to put province here
                        errorText={fieldsErrors.province}
                        placeholder={placeholder}
                        autoComplete={'address-level1'}
                      />
                    )
                  }

                  return (
                    <ExtUITextInput
                      key={name}
                      name={name}
                      testId={name}
                      onChange={handleFormChange}
                      value={fields[name as keyof AddressFormFields]}
                      placeholder={placeholder}
                      label={label}
                      errorText={fieldsErrors[name as keyof AddressFormFields]}
                    />
                  )
                })}
              {additionalFields?.length > 0 &&
                additionalFields.map((newField) => {
                  return (
                    <ExtUITextInput
                      key={newField.name}
                      name={newField.name}
                      testId={newField.name}
                      onChange={handleFormChange}
                      value={fields[newField.name as keyof AddressFormFields]}
                      label={newField.label}
                      placeholder={newField.label}
                      microcopy={newField.description}
                      errorText={
                        fieldsErrors[newField.name as keyof AddressFormFields]
                      }
                    />
                  )
                })}
            </div>
            <div className="flex-row justify-between">
              {previousElement}
              <Button
                type="submit"
                onSubmit={handleSubmit}
                data-testid="next-button"
              >
                {translate('Continue to Review and Confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}
